import { TABLE_TYPE } from 'components/PayrollTable/constants';
import { Button } from '@mui/material';
import { generateUserFullName } from 'utils/name';
import intl from 'react-intl-universal';
import { TotalPaidWageColumn } from 'views/Wages/commonColumns';
import PropTypes from 'prop-types';
import logger from 'utils/logger';
import {
  createWagesCSVContent,
  createDownloadLink,
  createCSVKenshinCSVContent,
} from '../shared/csv';
const log = logger.createLogger('PaidWageSummaryColumns');

export const getSummaryTableData = (paidWageData, paidWageDataKenshin) => {
  const totalWageMap = new Map();
  paidWageData.forEach((wage) => {
    const doctorId = wage.user.id;
    const totalWage = totalWageMap.get(doctorId);
    if (!totalWage) {
      const newTotalWage = {
        doctorName: generateUserFullName(
          wage.user.firstName,
          wage.user.lastName
        ),
        doctorId: doctorId,
        wageAmount: wage.currentWage ? wage.currentWage : wage.originalWage,
      };
      totalWageMap.set(doctorId, newTotalWage);
    } else {
      const newTotalWage = {
        doctorName: totalWage.doctorName,
        doctorId: doctorId,
        wageAmount: wage.currentWage
          ? totalWage.wageAmount + wage.currentWage
          : totalWage.wageAmount + wage.originalWage,
      };
      totalWageMap.set(doctorId, newTotalWage);
    }
  });

  paidWageDataKenshin.forEach((wage) => {
    const doctorId = wage.doctorId;
    const totalWage = totalWageMap.get(doctorId);
    if (!totalWage) {
      const newTotalWage = {
        doctorName: generateUserFullName(
          wage.doctorFirstName,
          wage.doctorLastName
        ),
        doctorId: doctorId,
        wageAmount: wage.currentWage ? wage.currentWage : wage.originalWage,
      };
      totalWageMap.set(doctorId, newTotalWage);
    } else {
      const newTotalWage = {
        doctorName: totalWage.doctorName,
        doctorId: doctorId,
        wageAmount: wage.currentWage
          ? totalWage.wageAmount + wage.currentWage
          : totalWage.wageAmount + wage.originalWage,
      };
      totalWageMap.set(doctorId, newTotalWage);
    }
  });

  const data = [];

  totalWageMap.forEach((value) => {
    const { doctorName, doctorId, wageAmount } = value;
    data.push({
      doctorName: doctorName,
      totalPaidWage: wageAmount,
      details: {
        doctorName: doctorName,
        doctorId: doctorId,
      },
      download: {
        doctorName: doctorName,
        doctorId: doctorId,
      },
    });
  });
  return data;
};

export const getSummaryTableColumns = (
  setSelectedDoctorName,
  setSelectedDoctorId,
  setTableType,
  paidWageData,
  paidWageDataKenshin,
  doesHaveKenshinPermission
) => [
  DoctorNameColumn(),
  TotalPaidWageColumn(),
  DetailsColumn({
    setSelectedDoctorName,
    setSelectedDoctorId,
    setTableType,
    doesHaveKenshinPermission,
  }),
  DownloadColumn({
    paidWageData,
    paidWageDataKenshin,
    doesHaveKenshinPermission,
  }),
];

const DoctorNameColumn = () => {
  return {
    accessorKey: 'doctorName',
    header: intl.get('paid_wage_label_column_doctor_name'),
    enableColumnFilter: true,
    enableSorting: true,
  };
};

const DetailsColumn = ({
  setSelectedDoctorName,
  setSelectedDoctorId,
  setTableType,
  doesHaveKenshinPermission,
}) => {
  return {
    accessorKey: 'details',
    header: intl.get('paid_wage_label_column_details'),
    enableColumnFilter: false,
    enableSorting: false,
    enableColumnActions: false,
    Cell: ({ renderedCellValue }) => {
      return detailsCell({
        renderedCellValue,
        setSelectedDoctorName,
        setSelectedDoctorId,
        setTableType,
        doesHaveKenshinPermission,
      });
    },
  };
};

const detailsCell = ({
  renderedCellValue,
  setSelectedDoctorName,
  setSelectedDoctorId,
  setTableType,
  doesHaveKenshinPermission,
}) => {
  const { doctorName, doctorId } = renderedCellValue;
  return (
    <div>
      <Button
        variant="contained"
        color="primary"
        onClick={() => {
          setSelectedDoctorName(doctorName);
          setSelectedDoctorId(doctorId);
          setTableType(TABLE_TYPE.DETAILED);
        }}>
        {intl.get('paid_wage_button_go_to_details_seimitsu')}
      </Button>
      {doesHaveKenshinPermission && (
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            setSelectedDoctorName(doctorName);
            setSelectedDoctorId(doctorId);
            setTableType(TABLE_TYPE.DETAILED_KENSHIN);
          }}>
          {intl.get('paid_wage_button_go_to_details_kenshin')}
        </Button>
      )}
    </div>
  );
};

detailsCell.propTypes = {
  renderedCellValue: PropTypes.shape({
    doctorName: PropTypes.string.isRequired,
    doctorId: PropTypes.string.isRequired,
  }).isRequired,
  setSelectedDoctorName: PropTypes.func.isRequired,
  setSelectedDoctorId: PropTypes.func.isRequired,
  setTableType: PropTypes.func.isRequired,
  doesHaveKenshinPermission: PropTypes.bool.isRequired,
};

const DownloadColumn = ({
  paidWageData,
  paidWageDataKenshin,
  doesHaveKenshinPermission,
}) => {
  const exportToCSV = (paidWageData, doctorId, doctorName) => {
    const selectedPaidWageData = paidWageData.filter(
      (paidWage) => paidWage.user.id === doctorId
    );
    const csvContent = createWagesCSVContent(selectedPaidWageData, true);
    const csvFilename = `paid_wage-${doctorName}.csv`;

    createDownloadLink(csvContent, csvFilename);
  };

  const exportToCSVKenshin = (paidWageDataKenshin, doctorId, doctorName) => {
    const selectedWageData = paidWageDataKenshin.filter(
      (wage) => wage.doctorId === doctorId
    );
    const csvContent = createCSVKenshinCSVContent(
      selectedWageData,
      true,
      doctorName
    );
    const csvFilename = `paid_wage-${doctorName}.csv`;
    createDownloadLink(csvContent, csvFilename);
  };

  return {
    accessorKey: 'download',
    header: intl.get('paid_wage_label_column_download'),
    enableColumnFilter: false,
    enableSorting: false,
    enableColumnActions: false,
    Cell: ({ renderedCellValue }) => {
      return downloadCell({
        renderedCellValue,
        exportToCSV,
        exportToCSVKenshin,
        paidWageData,
        paidWageDataKenshin,
        doesHaveKenshinPermission,
      });
    },
  };
};

const downloadCell = ({
  renderedCellValue,
  exportToCSV,
  exportToCSVKenshin,
  paidWageData,
  paidWageDataKenshin,
  doesHaveKenshinPermission,
}) => {
  const { doctorId, doctorName } = renderedCellValue;
  return (
    <div>
      <Button
        variant="contained"
        color="primary"
        onClick={() => {
          exportToCSV(paidWageData, doctorId, doctorName);
        }}>
        {intl.get('paid_wage_button_download_csv_seimitsu')}
      </Button>
      {doesHaveKenshinPermission && (
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            exportToCSVKenshin(paidWageDataKenshin, doctorId, doctorName);
          }}>
          {intl.get('paid_wage_button_download_csv_kenshin')}
        </Button>
      )}
    </div>
  );
};

downloadCell.propTypes = {
  renderedCellValue: PropTypes.shape({
    doctorId: PropTypes.string.isRequired,
    doctorName: PropTypes.string.isRequired,
  }).isRequired,
  exportToCSV: PropTypes.func.isRequired,
  exportToCSVKenshin: PropTypes.func.isRequired,
  paidWageData: PropTypes.array.isRequired,
  paidWageDataKenshin: PropTypes.array.isRequired,
  doesHaveKenshinPermission: PropTypes.bool.isRequired,
};
